import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import socketService from '../../services/socket';

// Thunks asynchrones
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/login', credentials);
      localStorage.setItem('access_token', response.data.data.token);
      return {
        user: response.data.data.user,
        tokens: { access: response.data.data.token }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur de connexion');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      // Filter out undefined, null, and empty string values
      const data = {};
      for (const [key, value] of Object.entries(userData)) {
        if (value !== undefined && value !== null && value !== '') {
          data[key] = value;
        }
      }
      // Ensure phone is string if present
      if (data.phone !== undefined) {
        data.phone = String(data.phone);
      }
      const response = await api.post('/register', data);
      localStorage.setItem('access_token', response.data.data.token);
      return {
        user: response.data.data.user,
        tokens: { access: response.data.data.token }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur d\'inscription');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async (otpData, { rejectWithValue }) => {
    try {
      const response = await api.post('/verify', otpData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Code OTP invalide');
    }
  }
);

export const verifyUnverified = createAsyncThunk(
  'auth/verifyUnverified',
  async ({ identifier, code }, { rejectWithValue }) => {
    try {
      const response = await api.post('/verify-unverified', { identifier, code });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Code OTP invalide');
    }
  }
);

export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async (identifier, { rejectWithValue }) => {
    try {
      const response = await api.post('/resend-verification', { identifier });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de l\'envoi du code de vérification');
    }
  }
);

export const socialLogin = createAsyncThunk(
  'auth/socialLogin',
  async ({ provider, token }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/auth/${provider.toLowerCase()}`, { token });
      localStorage.setItem('access_token', response.data.data.token);
      return {
        user: response.data.data.user,
        tokens: { access: response.data.data.token }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur de connexion sociale');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  return null;
});

export const fetchUser = createAsyncThunk(
  'auth/fetchUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/user');
      return response.data.data;
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return rejectWithValue(error.response?.data || 'Failed to fetch user');
    }
  }
);

const initialState = {
  user: localStorage.getItem('access_token') ? JSON.parse(localStorage.getItem('user') || 'null') : null,
  tokens: {
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  },
  isAuthenticated: !!localStorage.getItem('access_token'),
  loading: false,
  error: null,
  otpRequired: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.otpRequired = !action.payload.user.email_verified && !action.payload.user.phone_verified;
        localStorage.setItem('user', JSON.stringify(action.payload.user));

        // Connect to socket
        socketService.connect(action.payload.tokens.access);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.otpRequired = true;
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpRequired = false;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Social Login
      .addCase(socialLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.otpRequired = !action.payload.user.email_verified && !action.payload.user.phone_verified;
        localStorage.setItem('user', JSON.stringify(action.payload.user));

        // Connect to socket
        socketService.connect(action.payload.tokens.access);
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resend Verification
      .addCase(resendVerification.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendVerification.fulfilled, (state) => {
        state.loading = false;
        state.error = null; // Clear error on success
      })
      .addCase(resendVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.tokens = { access: null, refresh: null };
        state.isAuthenticated = false;
        state.otpRequired = false;
        localStorage.removeItem('user');

        // Disconnect socket
        socketService.disconnect();
      })
      // Fetch User
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer;
